import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import logoHorizontal from '@/assets/logo-horizontal-bk.svg';
import { getErrorMessage } from '@/lib/errors';

const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function RestablecerPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post('/api/auth/reset-password', { token, password: form.password }).then(r => r.data),
    onSuccess: () => setDone(true),
    onError: (e: any) => toast.error(getErrorMessage(e, 'El enlace no es válido o ha expirado')),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (form.password !== form.confirm) return toast.error('Las contraseñas no coinciden');
    mutation.mutate();
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={logoHorizontal} alt="Anik" className="h-10 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Restablecer contraseña</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {!token && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="text-red-400" size={32} />
              <p className="font-semibold text-gray-800">Enlace no válido</p>
              <p className="text-sm text-gray-500">Falta el token de restablecimiento. Solicita un nuevo enlace.</p>
              <button
                onClick={() => navigate('/olvide-password')}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                Solicitar nuevo enlace
              </button>
            </div>
          )}

          {done && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <CheckCircle2 className="text-emerald-500" size={40} />
              <p className="font-semibold text-gray-800">¡Contraseña actualizada!</p>
              <p className="text-sm text-gray-500">Ya puedes iniciar sesión con tu nueva contraseña.</p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                Ir al inicio de sesión
              </button>
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nueva contraseña *</label>
                <div className="relative">
                  <input
                    className={inputCls}
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirmar contraseña *</label>
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Repite tu contraseña"
                  value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                {mutation.isPending && <Loader2 size={15} className="animate-spin" />}
                Restablecer contraseña
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
